import { useCallback, useMemo } from "react";
import { SafeInput } from "../../components/SafeInput";
import { NUMBER_INPUT_REGEX } from "@src/config";
import { Skeleton } from "../../components/Skeleton";
import { useSwapContext } from "@src/contexts";
import { CashLogoWithChain } from "../../components/CashLogoWithChain";

type Props = {
  amount: string;
  setAmount: React.Dispatch<React.SetStateAction<string>>;
  maxAmount?: number;
  balance: string;
  isBalanceLoading: boolean;
  helperButtonsType: "dollar" | "percentage";
};

export const AmountPicker = ({
  amount,
  setAmount,
  maxAmount,
  balance,
  isBalanceLoading,
  helperButtonsType,
}: Props) => {
  const { chain, isMultichain } = useSwapContext();

  const amountValid = useMemo(() => {
    if (!maxAmount) return true;
    return Number(amount) <= maxAmount;
  }, [amount, maxAmount]);

  const renderPercentButton = useCallback(
    (percentage: number) => {
      return (
        <div
          onClick={() =>
            setAmount(((Number(balance) * percentage) / 100).toFixed(2))
          }
          className="bg-card_bg flex flex-1 rounded-full items-center justify-center cursor-pointer h-[30px]"
        >
          <div className="text-text_primary text-xs font-bold">
            {percentage}%
          </div>
        </div>
      );
    },
    [balance, setAmount],
  );

  const renderDollarButton = useCallback(
    (dollarAmount: number) => {
      return (
        <div
          onClick={() => setAmount(dollarAmount.toString())}
          className="bg-card_bg flex flex-1 rounded-full items-center justify-center cursor-pointer h-[30px]"
        >
          <div className="text-text_primary text-xs font-bold">
            ${dollarAmount}
          </div>
        </div>
      );
    },
    [setAmount],
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-center bg-card_bg rounded-[12px] h-[40px]">
        <div className="flex items-center gap-1">
          {isBalanceLoading ? (
            <Skeleton className="w-8 h-3" />
          ) : (
            <div className="text-xs font-bold text-text_primary">
              ${balance}
            </div>
          )}
          <CashLogoWithChain
            chain={isMultichain ? chain : undefined}
            size="small"
          />
          <div className="text-xs text-text_primary font-medium">Cash</div>
        </div>
      </div>
      <div
        className={`flex justify-center ${
          amountValid ? "bg-card_bg" : "bg-error_color/10"
        }  rounded-[12px] h-[90px] overflow-hidden px-4`}
      >
        <SafeInput
          id="amount-input"
          className={`bg-transparent border-none p-0 text-[48px] font-bold placeholder:text-[48px] placeholder:lh placeholder:text-text_primary placeholder:text-opacity-60 outline-0 w-full text-center`}
          regex={NUMBER_INPUT_REGEX(6)}
          value={amount}
          onChange={setAmount}
          placeholder={"$0"}
          prefix={"$"}
        />
      </div>
      <div className="flex  gap-3">
        {helperButtonsType === "percentage" ? (
          <>
            {renderPercentButton(25)}
            {renderPercentButton(50)}
            {renderPercentButton(75)}
            {renderPercentButton(100)}
          </>
        ) : (
          <>
            {renderDollarButton(50)}
            {renderDollarButton(100)}
            {renderDollarButton(150)}
            {renderDollarButton(200)}
          </>
        )}
      </div>
    </div>
  );
};
